import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { auditLogApi } from '../../../api/audit-log';
import { AuditLogEntry } from '../../../types';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/atoms/button';
import { Input } from '../../../components/ui/atoms/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/molecules/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/molecules/select';
import { UserCombobox } from '../../../components/ui/molecules/user-combobox';
import { AuditLogDetailDialog } from './components';
import { ACTIONS, ACTION_BADGE, formatTimestamp, targetTableLabel } from './utils';

export function AuditLogPage() {
  const [page, setPage] = useState(1);
  const [actorId, setActorId] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['audit-log', page, actorId, action, from, to],
    queryFn: () =>
      auditLogApi
        .list({
          page,
          limit: 20,
          actorId: actorId || undefined,
          action: action || undefined,
          from: from || undefined,
          to: to || undefined,
        })
        .then((r) => r.data),
  });

  const logs: AuditLogEntry[] = data?.data ?? [];
  const meta = data?.meta;

  const hasFilters = actorId || action || from || to;
  const reset = () => {
    setActorId(''); setAction(''); setFrom(''); setTo(''); setPage(1);
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Audit Log</h1>

      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Performed by</label>
            <UserCombobox
              value={actorId}
              onValueChange={(v) => { setActorId(v); setPage(1); }}
              allowEmpty
              emptyLabel="All users"
              placeholder="All users"
              className="w-44"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Action</label>
            <Select value={action} onValueChange={(v) => { setAction(v === '__all__' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All actions</SelectItem>
                {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">From</label>
            <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} className="w-36" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">To</label>
            <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} className="w-36" />
          </div>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={reset}>Clear</Button>
          )}
        </div>

        {isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Performed by</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      No audit log entries found.
                    </TableCell>
                  </TableRow>
                )}
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {formatTimestamp(log.createdAt)}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{log.actor?.name ?? '—'}</div>
                      <div className="text-xs text-muted-foreground">{log.actor?.email}</div>
                    </TableCell>
                    <TableCell>
                      <span className={cn('inline-flex text-xs font-medium px-2 py-0.5 rounded-full capitalize', ACTION_BADGE[log.action] ?? 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300')}>
                        {log.action}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{targetTableLabel(log.targetTable)}</div>
                      <div className="text-xs text-muted-foreground font-mono">{log.targetId.slice(0, 8)}…</div>
                    </TableCell>
                    <TableCell>
                      <AuditLogDetailDialog log={log} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {meta && meta.totalPages > 1 && (
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {meta.page} of {meta.totalPages}
                </span>
                <Button variant="outline" size="sm" disabled={page >= meta.totalPages} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
